import { useResizeSensor } from '@e2m-customer-portal/hooks';
import { Checkbox, CheckboxButton } from '@e2m-customer-portal/ui';
import { FormControlLabel, Typography } from '@mui/material';
import { addMonths, set } from 'date-fns';
import { range } from 'ramda';
import { Fragment, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui-compat';

const useStyles = makeStyles()((theme) => {
  const checkboxItemWidth = 28;
  const third = 100 / 3;
  return {
    grid: {
      display: 'flex',
      flexWrap: 'wrap',
      boxSizing: 'border-box',
    },
    item: {
      flexGrow: 0,
      margin: 0,
      boxSizing: 'border-box',
    },
    fullWidth: {
      maxWidth: '100%',
      flexBasis: '100%',
    },
    error: {
      color: theme.palette.error.main,
    },
    checkboxControl: {
      marginLeft: 0,
    },
    checkboxLabel: {
      fontWeight: 600,
    },
    allCheckbox: {
      marginLeft: '-6px',
    },
    checkboxItem: {
      position: 'relative',
      maxWidth: `${checkboxItemWidth}px`,
      flexBasis: `${checkboxItemWidth}px`,
    },
    checkbox: {
      position: 'absolute',
      marginLeft: '-6px',
      top: '50%',
      transform: 'translateY(-50%)',
    },
    buttonItem: {
      maxWidth: `calc(${third}% - ${checkboxItemWidth / 3}px)`,
      flexBasis: `calc(${third}% - ${checkboxItemWidth / 3}px)`,
      padding: '1px 2px 2px 1px',
    },
    hidden: {
      clip: 'rect(1px, 1px, 1px, 1px)',
      clipPath: 'inset(50%)',
      height: '1px',
      width: '1px',
      margin: '-1px',
      overflow: 'hidden',
      padding: '0px',
      position: 'absolute',
    },
  };
});

const WIDTH_BREAKPOINT = 310;

export type MonthSelection = Record<number, boolean>;

export const makeMonthSelection = (
  selected: MonthSelection | boolean,
  disabled?: MonthSelection | boolean,
): MonthSelection => {
  const monthIsEnabled = (month: number): boolean =>
    (typeof disabled !== 'object' && !disabled) || (typeof disabled === 'object' && !disabled[month]);

  return range(0, 12).reduce(
    (selection, index) => ({
      ...selection,
      [index]: (typeof selected === 'boolean' ? selected : selected[index]) && monthIsEnabled(index),
    }),
    {} as MonthSelection,
  );
};

export interface MonthSelectorProps {
  heading?: string;
  selected?: MonthSelection;
  onChange?: (selected: MonthSelection) => void;
  disabled?: MonthSelection | boolean;
  error?: boolean;
  helperText?: string;
}

export function MonthSelector({
  heading,
  selected,
  onChange,
  disabled,
  error,
  helperText,
}: Readonly<MonthSelectorProps>): ReactElement<MonthSelectorProps> {
  const { classes, cx } = useStyles();
  const [t] = useTranslation();
  const selection = selected ?? makeMonthSelection(false);
  const disabledMonths = typeof disabled === 'object' ? disabled : makeMonthSelection(!!disabled);

  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const width = useResizeSensor(ref)?.width ?? 0;

  const allDisabled = range(0, 12).every((month) => disabledMonths[month]);

  const allChecked = range(0, 12).every((month) => selection[month] || disabledMonths[month]) && !allDisabled;
  const someChecked = range(0, 12).some((month) => selection[month]);
  const handleAllChange = onChange && (() => onChange(makeMonthSelection(!allChecked, disabled)));

  const isLineChecked = (month: number): boolean =>
    range(month, month + 3).every((m) => selection[m] || disabledMonths[m]) &&
    range(month, month + 3).some((m) => !disabledMonths[m]);
  const isLineSomeChecked = (month: number): boolean => range(month, month + 3).some((m) => selection[m]);
  const isLineDisabled = (month: number): boolean => range(month, month + 3).every((m) => disabledMonths[m]);
  const handleLineChange = (month: number): undefined | (() => void) =>
    onChange &&
    (() => {
      const check = !isLineChecked(month);
      onChange({
        ...selection,
        ...range(month, month + 3).reduce((res, m) => ({ ...res, [m]: disabledMonths[m] ? false : check }), {}),
      });
    });

  const handleChange = (m: number): undefined | ((checked: boolean) => void) =>
    onChange && ((checked) => onChange({ ...selection, [m]: checked }));

  return (
    <div ref={setRef} className={classes.grid}>
      {heading && (
        <div className={cx(classes.item, classes.fullWidth, { [classes.error]: error })}>
          <Typography variant="body1">{heading}</Typography>
        </div>
      )}

      {helperText && (
        <div className={cx(classes.item, classes.fullWidth, { [classes.error]: error })}>
          <Typography variant="body1">{helperText}</Typography>
        </div>
      )}

      <div className={cx(classes.item, classes.fullWidth)}>
        <FormControlLabel
          className={classes.checkboxControl}
          classes={{ label: classes.checkboxLabel }}
          control={
            <Checkbox
              className={classes.allCheckbox}
              checked={allChecked}
              indeterminate={someChecked && !allChecked}
              disabled={typeof disabled === 'boolean' ? disabled : allDisabled}
              onChange={handleAllChange}
            />
          }
          label={t('MONTH_SELECTOR_SELECT_ALL')}
        />
      </div>

      {range(0, 12).map((month) => {
        const date = set(new Date(), {
          month,
          date: 1,
          hours: 0,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
        });
        return (
          <Fragment key={month}>
            {month % 3 === 0 && (
              <div className={cx(classes.item, classes.checkboxItem)}>
                <Checkbox
                  className={classes.checkbox}
                  checked={isLineChecked(month)}
                  indeterminate={!isLineChecked(month) && isLineSomeChecked(month)}
                  disabled={isLineDisabled(month)}
                  onChange={handleLineChange(month)}
                  ariaLabel={t('MONTH_SELECTOR_LINE', { start: date, end: addMonths(date, 2) })}
                />
              </div>
            )}
            <div className={cx(classes.item, classes.buttonItem)}>
              <CheckboxButton
                checked={selection[month]}
                disabled={disabledMonths[month]}
                onChange={handleChange(month)}
              >
                <span aria-hidden="true" className={cx({ [classes.hidden]: width > WIDTH_BREAKPOINT })}>
                  {t('MONTH_SELECTOR_CELL_SHORT', { date })}
                </span>
                <span className={cx({ [classes.hidden]: width <= WIDTH_BREAKPOINT })}>
                  {t('MONTH_SELECTOR_CELL', { date })}
                </span>
              </CheckboxButton>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
